import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { runtimeConfig } from '@/constants';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import useAppStore from '@/store/app.ts';
import { AgentCommand, type NotificationPayload } from '@/types/agent-realtime';
import type { CreatorChatFlowMessage } from '@/types/creator-chat-flow';
import { useGetActMessages, useSendUserMessageMutation } from '@/utils/api';
import {
	normalizeCreatorChatFlowMessages,
	normalizeCreatorChatFlowNotification
} from '../utils/creatorChatFlowHistory';

export const useCreatorChatFlowHistory = (enabled: boolean) => {
	const [isLoadingHistory, setIsLoadingHistory] = useState(false);
	const [messages, setMessages] = useState<
		ReturnType<typeof normalizeCreatorChatFlowMessages>
	>([]);

	const actor = useAppStore((state) => state.actor);
	const { mutateAsync: getActs } = useGetActMessages();
	const { isPending: isSendingMessage, mutateAsync: sendUserMessage } =
		useSendUserMessageMutation();
	const chatConfig = {
		clientSecret: runtimeConfig.creatorClientSecret,
		connectorIdn: 'newo_chat'
	};
	const appendMessage = useCallback((message: CreatorChatFlowMessage) => {
		setMessages((current) =>
			current.some((item) => item.id === message.id)
				? current
				: [...current, message]
		);
	}, []);

	const sendMessage = useCallback(
		async (text: string) => {
			const message = text.trim();
			if (!enabled || !message || !actor?.external_id || isSendingMessage) {
				return;
			}

			const optimisticMessage = {
				id: uuidv4(),
				role: 'user',
				text: message
			} satisfies CreatorChatFlowMessage;

			appendMessage(optimisticMessage);

			try {
				await sendUserMessage({
					actorId: actor.id,
					clientSecret: chatConfig.clientSecret,
					externalEventId: actor.external_id,
					message
				});
			} catch (error) {
				setMessages((current) =>
					current.filter((item) => item.id !== optimisticMessage.id)
				);
				throw error;
			}
		},
		[
			appendMessage,
			chatConfig.clientSecret,
			enabled,
			isSendingMessage,
			actor,
			sendUserMessage
		]
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: need to run only one time
	useEffect(() => {
		if (!enabled || !actor?.external_id) {
			return;
		}

		let isCancelled = false;
		const realtimeConfig = {
			...chatConfig,
			externalId: actor?.external_id
		};
		const appendRealtimeMessage = (notification: NotificationPayload) => {
			const message = normalizeCreatorChatFlowNotification(notification);

			if (!message) {
				return;
			}

			appendMessage(message);
		};

		const loadInitialHistory = async () => {
			try {
				setIsLoadingHistory(true);
				if (isCancelled || !actor?.id) {
					return;
				}

				const acts = await getActs(actor.id);

				setMessages(normalizeCreatorChatFlowMessages(acts));
			} catch {
			} finally {
				if (!isCancelled) {
					setIsLoadingHistory(false);
				}
			}
		};

		const offNotification = agentTrainingRealtime.onNotification(
			appendRealtimeMessage
		);
		agentTrainingRealtime.init(realtimeConfig, AgentCommand.OnboardingFinished);

		void loadInitialHistory();

		return () => {
			isCancelled = true;
			offNotification();
			agentTrainingRealtime.disconnect();
		};
	}, [
		enabled,
		chatConfig.clientSecret,
		chatConfig.connectorIdn,
		getActs,
		appendMessage
	]);

	return {
		isLoadingHistory,
		isSendingMessage,
		messages,
		sendMessage
	};
};
